import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaRobot, FaMobileAlt, FaCamera, FaApple, FaGooglePlay } from 'react-icons/fa';
import { theme, t } from '../theme';
import { AuthContext } from '../context/AuthContext';
import { isNative } from '../platform/native';
import { lojaRecomendada, LINK_IOS, LINK_ANDROID } from '../platform/lojas';
import { oQueMostrar, marcarVisto, jaViu } from './novidadesRegras';

/**
 * Aviso de lançamento, uma vez por navegador.
 *
 * DOIS PÚBLICOS, UM COMPONENTE: quem já usava o Metria precisa saber o que
 * mudou; quem acabou de criar a conta já nasce com tudo e só precisa saber que
 * existe app. Apresentar "novidades" a quem nunca viu o sistema antigo confunde
 * mais do que informa.
 *
 * Quem decide o público é `novidadesRegras.js` (puro e testado). Aqui é só a
 * tela. Os dois arquivos têm nomes diferentes de propósito: no Windows o
 * sistema de arquivos não distingue maiúsculas, e `novidades.js` colidiria com
 * `Novidades.js` — um sobrescreve o outro sem aviso nenhum.
 */
export default function Novidades() {
  const { tenant } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const [fechado, setFechado] = useState(false);

  const alvo = useMemo(() => oQueMostrar({
    tenantCriadoEm: tenant?.created_at,
    jaViu: jaViu(),
    dentroDoApp: isNative(),
  }), [tenant?.created_at]);

  const loja = useMemo(() => lojaRecomendada({
    nativo: isNative(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  }), []);

  if (!alvo || fechado) return null;

  const fechar = () => { marcarVisto(); setFechado(true); };

  const botoesDeLoja = () => {
    if (loja.loja === 'ambas') {
      // No computador não dá para saber o aparelho dela — mostra os dois.
      return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a href={LINK_IOS} target="_blank" rel="noreferrer" style={estiloLoja}><FaApple /> App Store</a>
          <a href={LINK_ANDROID} target="_blank" rel="noreferrer" style={estiloLoja}><FaGooglePlay /> Google Play</a>
        </div>
      );
    }
    if (!loja.url) return null;
    return (
      <a href={loja.url} target="_blank" rel="noreferrer"
        style={{ ...t.btnPrimary, textDecoration: 'none', display: 'inline-flex',
                 alignItems: 'center', gap: 8, justifyContent: 'center' }}>
        {loja.loja === 'ios' ? <FaApple /> : <FaGooglePlay />} Baixar o app
      </a>
    );
  };

  return (
    <div style={t.overlay} className="modal-overlay-mobile">
      <div style={{ ...t.modal, maxWidth: 460 }} className="modal-content-sheet">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: theme.colors.text }}>
            {alvo === 'mudancas' ? 'Novidades no Metria' : 'O Metria cabe no seu bolso'}
          </h3>
          <button onClick={fechar} aria-label="Fechar"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 18 }}>
            <FaTimes />
          </button>
        </div>

        {alvo === 'mudancas' ? (
          <>
            <p style={{ fontSize: 14, color: theme.colors.textSecondary, margin: '8px 0 18px' }}>
              Duas mudanças grandes desde a última vez que você entrou.
            </p>
            <Destaque icone={<FaRobot />} titulo="A Mai cadastra por você">
              Mande foto das suas receitas ou a planilha que já usa. Ela cadastra
              seus ingredientes, embalagens, tamanhos de bolo e recheios, numa conversa.
            </Destaque>
            <Destaque icone={<FaMobileAlt />} titulo="Metria no celular">
              O app chegou! Pedidos, estoque e a montagem do dia na palma da mão.
            </Destaque>
            <Destaque icone={<FaCamera />} titulo="Leitura por câmera">
              Aponte para o código de barras da embalagem para dar entrada ou saída,
              sem digitar nada.
            </Destaque>

            <div style={{ display: 'grid', gap: 8, marginTop: 20 }}>
              <button onClick={() => { fechar(); navigate('/primeiros-passos'); }}
                style={{ ...t.btnPrimary, width: '100%', padding: 13 }}>
                Conhecer a Mai
              </button>
              {botoesDeLoja()}
              <button onClick={fechar} style={{ ...t.btnSecondary, width: '100%' }}>
                Agora não
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 14, color: theme.colors.textSecondary, margin: '8px 0 18px' }}>
              Tudo o que você faz aqui também funciona no celular — e a leitura por
              câmera, para dar entrada e saída de estoque, só existe no app.
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              {botoesDeLoja()}
              <button onClick={fechar} style={{ ...t.btnSecondary, width: '100%' }}>
                Depois eu baixo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const estiloLoja = {
  ...t.btnSecondary, textDecoration: 'none', display: 'inline-flex',
  alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center',
};

const Destaque = ({ icone, titulo, children }) => (
  <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-start' }}>
    <div style={{
      flexShrink: 0, width: 34, height: 34, borderRadius: 10,
      background: theme.colors.primaryLight, color: theme.colors.primary,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
    }}>{icone}</div>
    <div>
      <div style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text }}>{titulo}</div>
      <div style={{ fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.5 }}>{children}</div>
    </div>
  </div>
);
